import { CompanionState, RegisteredObject } from '../types/companion';

const STORAGE_KEYS = {
  COMPANION_STATE: 'companion-state',
  REGISTERED_OBJECTS: 'registered-objects',
  FIRST_VISIT: 'first-visit',
  INSTALL_PROMPT_SHOWN: 'install-prompt-shown',
  ONBOARDING_COMPLETED: 'onboarding-completed',
} as const;

class LocalStorageManager {
  private isAvailable(): boolean {
    try {
      const test = 'test-storage';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  private safeGetItem<T>(key: string, defaultValue: T): T {
    if (!this.isAvailable()) return defaultValue;
    
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Failed to parse localStorage item "${key}":`, error);
      return defaultValue;
    }
  }

  private safeSetItem<T>(key: string, value: T): boolean {
    if (!this.isAvailable()) return false;
    
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.warn(`Failed to save to localStorage "${key}":`, error);
      return false;
    }
  }

  // Companion State Management
  getCompanionState(): CompanionState {
    const registeredObjects = this.getRegisteredObjects();
    const isFirstVisit = this.isFirstVisit();
    const showInstallPrompt = this.shouldShowInstallPrompt();
    const hasCompletedOnboarding = this.hasCompletedOnboarding();

    return {
      registeredObjects,
      isFirstVisit,
      showInstallPrompt,
      hasCompletedOnboarding,
    };
  }

  // Registered Objects
  getRegisteredObjects(): RegisteredObject[] {
    return this.safeGetItem(STORAGE_KEYS.REGISTERED_OBJECTS, []);
  }

  saveRegisteredObjects(objects: RegisteredObject[]): boolean {
    return this.safeSetItem(STORAGE_KEYS.REGISTERED_OBJECTS, objects);
  }

  addRegisteredObject(object: RegisteredObject): boolean {
    const objects = this.getRegisteredObjects();
    const existingIndex = objects.findIndex(obj => obj.id === object.id);
    
    if (existingIndex >= 0) {
      // Update existing object
      objects[existingIndex] = object;
    } else {
      // Add new object
      objects.push(object);
    }
    
    return this.saveRegisteredObjects(objects);
  }

  updateRegisteredObject(id: string, updates: Partial<RegisteredObject>): boolean {
    const objects = this.getRegisteredObjects();
    const index = objects.findIndex(obj => obj.id === id);
    
    if (index >= 0) {
      objects[index] = { ...objects[index], ...updates };
      return this.saveRegisteredObjects(objects);
    }
    
    return false;
  }

  removeRegisteredObject(id: string): boolean {
    const objects = this.getRegisteredObjects();
    const filtered = objects.filter(obj => obj.id !== id);
    return this.saveRegisteredObjects(filtered);
  }

  getRegisteredObject(id: string): RegisteredObject | null {
    const objects = this.getRegisteredObjects();
    return objects.find(obj => obj.id === id) || null;
  }

  // First Visit Tracking
  isFirstVisit(): boolean {
    return this.safeGetItem(STORAGE_KEYS.FIRST_VISIT, true);
  }

  markFirstVisitComplete(): boolean {
    return this.safeSetItem(STORAGE_KEYS.FIRST_VISIT, false);
  }

  // Install Prompt Management
  shouldShowInstallPrompt(): boolean {
    return !this.safeGetItem(STORAGE_KEYS.INSTALL_PROMPT_SHOWN, false);
  }

  markInstallPromptShown(): boolean {
    return this.safeSetItem(STORAGE_KEYS.INSTALL_PROMPT_SHOWN, true);
  }

  resetInstallPrompt(): boolean {
    return this.safeSetItem(STORAGE_KEYS.INSTALL_PROMPT_SHOWN, false);
  }

  // Onboarding Management
  hasCompletedOnboarding(): boolean {
    return this.safeGetItem(STORAGE_KEYS.ONBOARDING_COMPLETED, false);
  }

  markOnboardingComplete(): boolean {
    return this.safeSetItem(STORAGE_KEYS.ONBOARDING_COMPLETED, true);
  }

  resetOnboarding(): boolean {
    return this.safeSetItem(STORAGE_KEYS.ONBOARDING_COMPLETED, false);
  }

  // Utility Methods
  generateObjectId(): string {
    // Generate 8-character random ID using base58 (excludes confusing chars: 0, O, I, l)
    const base58Chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
    let id = '';
    for (let i = 0; i < 8; i++) {
      id += base58Chars[Math.floor(Math.random() * base58Chars.length)];
    }
    return `_${id}`;
  }

  generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  getCurrentISOTimestamp(): string {
    return new Date().toISOString();
  }

  // Development/Debug Methods
  clearAllData(): boolean {
    if (!this.isAvailable()) return false;
    
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.warn('Failed to clear all localStorage data:', error);
      return false;
    }
  }

  exportData(): CompanionState | null {
    try {
      return this.getCompanionState();
    } catch (error) {
      console.warn('Failed to export companion data:', error);
      return null;
    }
  }

  importData(state: Partial<CompanionState>): boolean {
    try {
      if (state.registeredObjects) {
        this.saveRegisteredObjects(state.registeredObjects);
      }
      
      if (state.isFirstVisit !== undefined) {
        this.safeSetItem(STORAGE_KEYS.FIRST_VISIT, state.isFirstVisit);
      }
      
      if (state.showInstallPrompt !== undefined) {
        this.safeSetItem(STORAGE_KEYS.INSTALL_PROMPT_SHOWN, !state.showInstallPrompt);
      }

      if (state.hasCompletedOnboarding !== undefined) {
        this.safeSetItem(STORAGE_KEYS.ONBOARDING_COMPLETED, state.hasCompletedOnboarding);
      }
      
      return true;
    } catch (error) {
      console.warn('Failed to import companion data:', error);
      return false;
    }
  }
}

// Export singleton instance
export const storage = new LocalStorageManager();

// Convenience functions for common operations
export const getRegisteredObjects = () => storage.getRegisteredObjects();
export const addRegisteredObject = (object: RegisteredObject) => storage.addRegisteredObject(object);
export const updateRegisteredObject = (id: string, updates: Partial<RegisteredObject>) => 
  storage.updateRegisteredObject(id, updates);
export const removeRegisteredObject = (id: string) => storage.removeRegisteredObject(id);
export const generateObjectId = () => storage.generateObjectId();
export const generateMessageId = () => storage.generateMessageId();